import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useAuth } from "@/contexts/AuthContext";
import { motion } from "framer-motion";
import { Leaf, BookOpen, Sparkles, Compass, ArrowRight, Quote, Shield, Heart, Star, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { trackEvent } from "@/lib/posthog";
// import appPreview from "@/assets/app-preview.jpg"; // hidden until better image available

const features = [
  {
    icon: BookOpen,
    title: "Your private journal.",
    copy: "Write freely in a space that's entirely yours. Every entry is private, encrypted, and waiting for you whenever you need it.",
  },
  {
    icon: Sparkles,
    title: "Daily reflections.",
    copy: "Short, meaningful prompts that help you pause and process. Build a habit of self-awareness one reflection at a time.",
  },
  {
    icon: Compass,
    title: "Guided sessions.",
    copy: "Structured journeys led by AI that take you deeper. Explore anxiety, identity, gratitude, and more at your own pace.",
  },
];

const testimonials = [
  {
    quote: "I've tried a dozen journaling apps. This one actually made me think.",
    author: "M.R.",
  },
  {
    quote: "The guided sessions helped me work through something I'd been avoiding for months.",
    author: "J.L.",
  },
  {
    quote: "5 minutes a day changed how I start my mornings.",
    author: "A.K.",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.6, ease: [0.25, 0.1, 0.25, 1] as const },
  }),
};

export default function LandingPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [freeText, setFreeText] = useState("");

  useEffect(() => {
    if (!loading && user) {
      navigate("/dashboard", { replace: true });
    }
  }, [user, loading, navigate]);

  if (loading || user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Helmet>
        <title>InnerPath — A quiet place to hear yourself think</title>
        <meta name="description" content="Guided journaling, gentle AI reflections, and mood awareness — private by design. Start a free reflection, no account needed." />
        <link rel="canonical" href="https://myinnerpath.app/" />
        <meta property="og:title" content="InnerPath — A quiet place to hear yourself think" />
        <meta property="og:description" content="Guided journaling, gentle AI reflections, and mood awareness — private by design." />
        <meta property="og:url" content="https://myinnerpath.app/" />
      </Helmet>
      {/* Nav */}
      <nav className="sticky top-0 z-50 border-b border-border/50 bg-background/80 backdrop-blur-md">
        <div className="mx-auto max-w-6xl px-4 py-3 sm:flex sm:items-center sm:justify-between sm:px-6 sm:py-4">
          <div className="flex items-center justify-between sm:contents">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10">
              <Leaf className="h-5 w-5 text-primary" />
            </div>
            <span className="font-display text-xl font-semibold">InnerPath</span>
          </Link>
          <Button asChild className="rounded-xl sm:hidden">
            <Link to="/try" onClick={() => trackEvent("cta_clicked", { location: "nav_mobile", action: "try_guest_primary" })}>Try free</Link>
          </Button>
          </div>
          <div className="mt-2 flex items-center justify-between gap-1 border-t border-border/50 pt-2 sm:mt-0 sm:justify-end sm:gap-3 sm:border-0 sm:pt-0">
            <Button variant="ghost" asChild className="h-9 rounded-xl px-2 text-xs sm:h-10 sm:px-4 sm:text-sm">
              <Link to="/blog" onClick={() => trackEvent("cta_clicked", { location: "nav", action: "resources" })}>Resources</Link>
            </Button>
            <Button variant="ghost" asChild className="h-9 rounded-xl px-2 text-xs sm:h-10 sm:px-4 sm:text-sm">
              <Link to="/prompt-generator" onClick={() => trackEvent("cta_clicked", { location: "nav", action: "prompt_generator" })}>Prompt generator</Link>
            </Button>
            <Button variant="ghost" asChild className="rounded-xl hidden sm:inline-flex">
              <Link to="/try" onClick={() => trackEvent("cta_clicked", { location: "nav", action: "try_guest" })}>Try a reflection</Link>
            </Button>
            <Button variant="ghost" asChild className="h-9 rounded-xl px-2 text-xs sm:h-10 sm:px-4 sm:text-sm">
              <Link to="/auth" onClick={() => trackEvent("cta_clicked", { location: "nav", action: "login" })}>Log in</Link>
            </Button>
            <Button asChild className="hidden rounded-xl sm:inline-flex">
              <Link to="/try" onClick={() => trackEvent("cta_clicked", { location: "nav", action: "try_guest_primary" })}>Try free</Link>
            </Button>
          </div>
        </div>
      </nav>

      <main>
      {/* Hero */}
       <section className="mx-auto max-w-4xl px-6 pb-20 pt-12 text-center sm:pt-20 lg:pt-28">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="mx-auto inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-4 py-1.5 text-sm font-medium text-primary"
        >
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary/60 opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
          </span>
          Private. No signup. No AI training.
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.05 }}
          className="mt-6 font-display text-4xl font-semibold leading-tight tracking-tight sm:text-5xl lg:text-6xl"
        >
          Feel better in 2 minutes.{" "}
          <br className="hidden sm:block" />
          Pick how you feel and get a free reflection.
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.7 }}
          className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground sm:text-xl"
        >
          A calm, private space for guided journaling and gentle AI reflection. No account needed to
          try your first one.
        </motion.p>

        {/* Pre-signup reflection box */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25, duration: 0.6 }}
          className="mx-auto mt-10 max-w-2xl"
        >
          <h2 className="mb-3 text-center font-display text-xl font-semibold">How are you feeling right now?</h2>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const text = freeText.trim();
              if (!text) return;
              trackEvent("landing_prompt_typed", { destination: "guest_reflection" });
              navigate(`/try?prompt=${encodeURIComponent(text)}`);
            }}
            className="relative rounded-2xl border border-border/60 bg-card shadow-md transition-all focus-within:border-primary/50 focus-within:shadow-lg"
          >
            <textarea
              value={freeText}
              onChange={(e) => setFreeText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  e.currentTarget.form?.requestSubmit();
                }
              }}
              placeholder={'Share what\'s on your mind… e.g. "I can\'t stop overthinking a conversation from today"'}
              rows={3}
              aria-label="Describe how you're feeling to start a free reflection"
              className="w-full resize-none rounded-2xl bg-transparent px-5 py-4 pr-14 text-base leading-relaxed text-foreground placeholder:text-muted-foreground/60 focus:outline-none"
            />
            <button
              type="submit"
              disabled={!freeText.trim()}
              aria-label="Start a free reflection"
              className="absolute bottom-3 right-3 flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-30"
            >
              <Send className="h-4 w-4" />
            </button>
          </form>
          <p className="mt-2.5 text-center text-xs text-muted-foreground">
            Free, private, no account needed — a gentle reflection comes straight back.
          </p>

          <div className="mt-5 flex flex-wrap justify-center gap-2">
            {[
              { v: "anxious", e: "😰", l: "Anxious", preview: "When anxiety shows up, what's usually underneath it?" },
              { v: "overwhelmed", e: "😵", l: "Overwhelmed", preview: "If you could put one thing down right now, what would it be?" },
              { v: "sad", e: "😢", l: "Sad", preview: "What are you grieving today, even if it's small?" },
              { v: "lonely", e: "😔", l: "Lonely", preview: "What kind of connection are you really craving?" },
              { v: "grateful", e: "🙏", l: "Grateful", preview: "What is one small thing that went better than expected today?" },
              { v: "hopeful", e: "🌱", l: "Hopeful", preview: "What are you looking forward to this week?" },
            ].map((m) => (
              <button
                key={m.v}
                type="button"
                onClick={() => {
                  try { localStorage.setItem("ip_pending_mood", m.v); } catch { /* noop */ }
                  trackEvent("landing_mood_selected", { mood: m.v, destination: "guest_reflection" });
                  navigate(`/try?mood=${m.v}`);
                }}
                className="group relative flex items-center gap-1.5 rounded-full border border-border/60 bg-background/60 px-3 py-1.5 text-sm text-muted-foreground transition-all hover:border-primary/60 hover:bg-primary/5 hover:text-foreground"
              >
                <span>{m.e}</span> {m.l}
                <span className="pointer-events-none absolute -bottom-9 left-1/2 z-10 hidden w-56 -translate-x-1/2 rounded-lg border border-border/60 bg-card px-3 py-2 text-xs text-muted-foreground shadow-sm group-hover:block sm:w-64">
                  {m.preview}
                </span>
              </button>
            ))}
          </div>
          <p className="mt-2 text-center text-xs text-muted-foreground/70">
            Or tap a feeling above — hover to preview the reflection.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          className="mt-8 flex flex-col items-center gap-3"
        >
          <Button asChild size="lg" className="rounded-xl px-8 text-base">
            <Link to="/try" onClick={() => trackEvent("cta_clicked", { location: "hero", action: "try_guest" })}>
              Try a free reflection <ArrowRight className="ml-1 h-4 w-4" />
            </Link>
          </Button>
          <Link
            to="/auth"
            onClick={() => trackEvent("cta_clicked", { location: "hero", action: "login_secondary" })}
            className="text-sm text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
          >
            Already have an account? Log in
          </Link>
        </motion.div>


        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.6 }}
          className="mx-auto mt-6 max-w-md text-sm text-muted-foreground"
        >
          Free to start. No card required. Your entries are encrypted and never used to train AI.
        </motion.p>

        {/* Trust strip */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.6 }}
          className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-xs text-muted-foreground"
        >
          <span className="flex items-center gap-1.5">
            <Shield className="h-3.5 w-3.5 text-primary" />
            End-to-end private
          </span>
          <span className="flex items-center gap-1.5">
            <Heart className="h-3.5 w-3.5 text-primary" />
            No ads, ever
          </span>
          <span className="flex items-center gap-1.5">
            <Star className="h-3.5 w-3.5 text-primary" />
            Loved by reflective minds
          </span>
        </motion.div>

        {/* App preview */}
        {/* App preview – hidden for now */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.8 }}
          className="mt-16 hidden"
        >
          <div className="mx-auto max-w-3xl overflow-hidden rounded-2xl border border-border/50 bg-card shadow-lg">
            <div className="flex items-center gap-1.5 border-b border-border/30 px-4 py-3">
              <div className="h-2.5 w-2.5 rounded-full bg-destructive/40" />
              <div className="h-2.5 w-2.5 rounded-full bg-mood-hopeful/40" />
              <div className="h-2.5 w-2.5 rounded-full bg-primary/40" />
            </div>
            {/* Image removed until a better one is available */}
          </div>
        </motion.div>
      </section>

      {/* How it works */}
      <section className="border-t border-border/30 bg-card/30 py-20">
        <div className="mx-auto max-w-6xl px-6">
          <motion.h2
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeUp}
            custom={0}
            className="text-center font-display text-3xl font-semibold sm:text-4xl"
          >
            How it works
          </motion.h2>
          <p className="mx-auto mt-4 max-w-2xl text-center text-muted-foreground">
            No signup, no setup. Just a private space to check in with yourself.
          </p>

          <div className="mt-12 grid gap-8 sm:grid-cols-3">
            {[
              {
                step: "1",
                title: "Pick a mood",
                copy: "Tap how you're feeling. We'll match you with a prompt that fits.",
              },
              {
                step: "2",
                title: "Write freely",
                copy: "Answer the prompt in your own words. There's no wrong way to do it.",
              },
              {
                step: "3",
                title: "Get a gentle reflection",
                copy: "Our AI offers a supportive perspective — not advice, just a mirror for your thoughts.",
              },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-60px" }}
                variants={fadeUp}
                custom={i + 1}
                className="text-center"
              >
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 font-display text-lg font-semibold text-primary">
                  {item.step}
                </div>
                <h3 className="mt-4 font-display text-lg font-semibold">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.copy}</p>
              </motion.div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Button asChild size="lg" className="rounded-xl px-8 text-base">
              <Link
                to="/try"
                onClick={() => trackEvent("cta_clicked", { location: "how_it_works", action: "try_guest" })}
              >
                Try your first reflection <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="border-t border-border/30 bg-card/50 py-24">
        <div className="mx-auto max-w-6xl px-6">
          <motion.h2
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeUp}
            custom={0}
            className="text-center font-display text-3xl font-semibold sm:text-4xl"
          >
            Everything you need for your inner work
          </motion.h2>

          <div className="mt-16 grid gap-8 sm:grid-cols-3">
            {features.map((f, i) => (
              <motion.div
                key={f.title}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-60px" }}
                variants={fadeUp}
                custom={i + 1}
                className="glass-card p-8 text-center"
              >
                <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
                  <f.icon className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-display text-lg font-semibold">{f.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.copy}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Social proof – hidden for now, keep in codebase for future reference
      <section className="py-24">
        <div className="mx-auto max-w-6xl px-6">
          <motion.p
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            custom={0}
            className="text-center font-display text-2xl font-medium text-foreground sm:text-3xl"
          >
            What people are saying
          </motion.p>

          <div className="mt-12 grid gap-6 sm:grid-cols-3">
            {testimonials.map((t, i) => (
              <motion.div
                key={t.author}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-60px" }}
                variants={fadeUp}
                custom={i + 1}
                className="glass-card p-6"
              >
                <Quote className="mb-3 h-5 w-5 text-primary/40" />
                <p className="text-sm leading-relaxed text-foreground/90 italic">"{t.quote}"</p>
                <p className="mt-4 text-xs font-medium text-muted-foreground">— {t.author}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      */}

      {/* Browse prompts by mood */}
      <section className="border-t border-border/30 bg-background py-20">
        <div className="mx-auto max-w-6xl px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeUp}
            custom={0}
            className="text-center"
          >
            <h2 className="font-display text-3xl font-semibold sm:text-4xl">Prompts for every feeling</h2>
            <p className="mx-auto mt-4 max-w-2xl text-muted-foreground">
              Free, practical guides to help you journal through whatever you're experiencing.
            </p>
          </motion.div>

          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { slug: "/journal-prompts-for-anxiety", emoji: "😰", title: "Anxiety prompts", desc: "Calm anxious thoughts and stop overthinking." },
              { slug: "/gratitude-journal-prompts", emoji: "🙏", title: "Gratitude prompts", desc: "Train your attention to notice the good." },
              { slug: "/journaling-for-stress", emoji: "😵", title: "Stress relief", desc: "Release tension and clear your mind." },
              { slug: "/cbt-thought-record", emoji: "🧠", title: "CBT thought record", desc: "Challenge anxious and self-critical thoughts." },
              { slug: "/how-to-start-journaling", emoji: "🌿", title: "How to start journaling", desc: "A beginner's guide with prompts and habit tips." },
              { slug: "/shadow-work-journal-prompts", emoji: "🌑", title: "Shadow work prompts", desc: "Gently explore the parts of yourself you usually avoid." },
              { slug: "/morning-journal-prompts", emoji: "🌅", title: "Morning prompts", desc: "Start the day clear and grounded in five minutes." },
              { slug: "/journaling-before-bed", emoji: "🌙", title: "Journaling before bed", desc: "Quiet racing thoughts and sleep easier." },
              { slug: "/what-is-a-guided-journal", emoji: "📖", title: "What is a guided journal?", desc: "How prompts make journaling easier to stick with." },
              { slug: "/prompt-generator", emoji: "✨", title: "Prompt generator", desc: "Generate personalized prompts in one tap." },
            ].map((item, i) => (
              <motion.div
                key={item.slug}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-60px" }}
                variants={fadeUp}
                custom={i + 1}
              >
                <Link
                  to={item.slug}
                  onClick={() => trackEvent("landing_prompts_clicked", { page: item.slug })}
                  className="group flex h-full flex-col rounded-2xl border border-border/40 bg-card/50 p-5 transition-colors hover:bg-card"
                >
                  <div className="flex items-start justify-between">
                    <span className="text-2xl">{item.emoji}</span>
                    <ArrowRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-1" />
                  </div>
                  <h3 className="mt-3 font-display text-lg font-semibold">{item.title}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{item.desc}</p>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="border-t border-border/30 bg-card/50 py-24">
        <div className="mx-auto max-w-2xl px-6 text-center">
          <motion.h2
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            custom={0}
            className="font-display text-3xl font-semibold sm:text-4xl"
          >
            Start your inner journey today.
          </motion.h2>
          <motion.p
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            custom={1}
            className="mt-4 text-muted-foreground"
          >
            Free to begin. Deeper when you're ready.
          </motion.p>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            custom={2}
          >
            <Button asChild size="lg" className="mt-8 rounded-xl px-8 text-base">
              <Link to="/try" onClick={() => trackEvent("cta_clicked", { location: "footer_cta", action: "try_guest" })}>
                Try a free reflection <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>

          </motion.div>
        </div>
      </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-border/30 py-8">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 px-6 text-sm text-muted-foreground sm:flex-row">
          <p>InnerPath © {new Date().getFullYear()}</p>
          <div className="flex gap-6">
            <Link to="/blog" className="hover:text-foreground transition-colors">Resources</Link>
            <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-foreground transition-colors">Terms of Service</Link>
            <Link to="/contact" className="hover:text-foreground transition-colors">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
